import * as React from 'react'

import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { IPermitTypeField } from '~/client/graph'
import { isFieldMultiple } from '~/client/src/shared/constants/PermitFieldTypeConstants'
import IPermitFieldsStore from '~/client/src/shared/models/IPermitFieldsStore'

import SitePermitCreationFormStore from '../../WorkflowCreationForm.store'
import WorkflowFormAddFieldButton from './components/WorkflowFormAddFieldButton'
import WorkflowFormCustomFieldValue from './components/WorkflowFormCustomFieldValue'
import WorkflowFormFieldHeader from './components/WorkflowFormFieldHeader'
import WorkflowFormFieldValue from './components/WorkflowFormFieldValue'

interface IProps<T> {
  typeField: IPermitTypeField
  fieldValues: T[]
  isViewMode: boolean
  componentRenderer(
    value: T,
    index: number,
    isHeaderShown?: boolean,
  ): JSX.Element

  store: SitePermitCreationFormStore
  fieldsStore: IPermitFieldsStore

  isForcedTitle?: boolean
  isHeaderForced?: boolean
  headerActions?: JSX.Element
  footerContent?: JSX.Element
  isViewModeIgnored?: boolean
  isViewModeContentFullWidth?: boolean
  parentInstanceId?: string
  parentValueIndex?: number
  viewModeRenderer?(value: T): JSX.Element

  titleRenderer?(value: T, index: number): JSX.Element | null
  onAddFieldClick?(): void
  onRemoveField?(index: number): void
}

@observer
export default class PermitBaseFormField<T> extends React.Component<IProps<T>> {
  @observable private areValuesHidden = false
  @observable private localProps: IProps<T> = null

  public constructor(props: IProps<T>) {
    super(props)
    this.localProps = props
    makeObservable(this)
  }

  public componentDidUpdate(prevProps: Readonly<IProps<T>>) {
    runInAction(() => {
      if (this.props.fieldValues !== prevProps.fieldValues) {
        this.localProps.fieldValues = this.props.fieldValues
      }
      if (this.props.typeField !== prevProps.typeField) {
        this.localProps.typeField = this.props.typeField
      }
      if (this.props.componentRenderer !== prevProps.componentRenderer) {
        this.localProps.componentRenderer = this.props.componentRenderer
      }
      if (this.props.viewModeRenderer !== prevProps.viewModeRenderer) {
        this.localProps.viewModeRenderer = this.props.viewModeRenderer
      }
      if (this.props.isViewMode !== prevProps.isViewMode) {
        this.localProps.isViewMode = this.props.isViewMode
      }
      if (this.props.isForcedTitle !== prevProps.isForcedTitle) {
        this.localProps.isForcedTitle = this.props.isForcedTitle
      }
      if (this.props.isHeaderForced !== prevProps.isHeaderForced) {
        this.localProps.isHeaderForced = this.props.isHeaderForced
      }
      if (this.props.isViewModeIgnored !== prevProps.isViewModeIgnored) {
        this.localProps.isViewModeIgnored = this.props.isViewModeIgnored
      }
      if (
        this.props.isViewModeContentFullWidth !==
        prevProps.isViewModeContentFullWidth
      ) {
        this.localProps.isViewModeContentFullWidth =
          this.props.isViewModeContentFullWidth
      }
      if (this.props.parentInstanceId !== prevProps.parentInstanceId) {
        this.localProps.parentInstanceId = this.props.parentInstanceId
      }
      if (this.props.parentValueIndex !== prevProps.parentValueIndex) {
        this.localProps.parentValueIndex = this.props.parentValueIndex
      }
      if (this.props.titleRenderer !== prevProps.titleRenderer) {
        this.localProps.titleRenderer = this.props.titleRenderer
      }
      if (this.props.headerActions !== prevProps.headerActions) {
        this.localProps.headerActions = this.props.headerActions
      }
      if (this.props.footerContent !== prevProps.footerContent) {
        this.localProps.footerContent = this.props.footerContent
      }
      if (this.props.onAddFieldClick !== prevProps.onAddFieldClick) {
        this.localProps.onAddFieldClick = this.props.onAddFieldClick
      }
    })
  }

  public render() {
    const {
      typeField,
      children,
      fieldValues,
      parentInstanceId,
      parentValueIndex,
      isViewMode,
      isViewModeIgnored,
      store,
      fieldsStore,
      viewModeRenderer,
      onAddFieldClick,
    } = this.props

    if (children) {
      return (
        <WorkflowFormCustomFieldValue
          children={children}
          typeField={typeField}
          fieldValues={fieldValues}
          isViewMode={isViewMode}
          isAddButtonShown={this.isAddButtonShown}
          isViewModeIgnored={isViewModeIgnored}
          parentInstanceId={parentInstanceId}
          conditionalParentInstanceId={this.conditionalParentInstanceId}
          parentValueIndex={parentValueIndex}
          store={store}
          fieldsStore={fieldsStore}
          viewModeRenderer={viewModeRenderer}
          onAddFieldClick={onAddFieldClick}
        />
      )
    }

    return (
      <div
        className={classList({
          'relative col': true,
          'mt10 mb10 brada8 ba-grey-80 overflow-hidden':
            this.isFieldHeaderShown,
          pb12: isViewMode && !this.areValuesHidden,
        })}
      >
        <WorkflowFormFieldHeader
          isShown={this.isFieldHeaderShown}
          fieldCaption={typeField.caption}
          areValuesHidden={this.areValuesHidden}
          actions={this.localProps.headerActions}
          toggleValuesHidden={this.toggleValuesHidden}
        />
        {this.fieldValuesRenderer}
        <WorkflowFormAddFieldButton
          isShown={this.isAddButtonShown}
          title={typeField.caption}
          onAddFieldClick={onAddFieldClick}
        />
        {!this.areValuesHidden && this.localProps.footerContent}
      </div>
    )
  }

  @computed
  private get fieldValuesRenderer(): JSX.Element[] {
    if (this.areValuesHidden) return null

    const {
      fieldValues,
      typeField,
      fieldsStore,
      parentInstanceId,
      parentValueIndex,
      store,
      isViewMode,
      isForcedTitle,
      isViewModeContentFullWidth,
      componentRenderer,
      onRemoveField,
      viewModeRenderer,
    } = this.localProps

    return fieldValues.map((value, index) => {
      const conditionalFields = fieldsStore.getAvailableConditionalFields(
        typeField,
        index,
        parentInstanceId,
        parentValueIndex,
      )

      return (
        <WorkflowFormFieldValue
          key={`field-value-${index}`}
          value={value}
          index={index}
          typeField={typeField}
          conditionalFields={conditionalFields}
          conditionalParentInstanceId={this.conditionalParentInstanceId}
          isViewMode={isViewMode}
          hasMultipleValues={this.hasMultipleValues}
          isTitleShown={this.hasAnyConditionals || isForcedTitle}
          componentRenderer={componentRenderer}
          store={store}
          fieldsStore={fieldsStore}
          viewModeRenderer={viewModeRenderer}
          isViewModeContentFullWidth={isViewModeContentFullWidth}
          titleRenderer={this.localProps.titleRenderer}
          onRemoveField={onRemoveField}
        />
      )
    })
  }

  private get hasMultipleValues(): boolean {
    return this.isMultiple && this.localProps.fieldValues?.length > 1
  }

  private get isMultiple(): boolean {
    const { onAddFieldClick } = this.localProps
    const { isMultiple } = this.localProps.typeField

    return isFieldMultiple(isMultiple) && typeof onAddFieldClick === 'function'
  }

  @computed
  private get conditionalParentInstanceId(): string {
    const { fieldsStore, parentInstanceId, parentValueIndex, typeField } =
      this.localProps

    return fieldsStore.editableEntity.getField(
      typeField.id,
      parentInstanceId,
      parentValueIndex,
    )?.instanceId
  }

  @computed
  private get isAddButtonShown(): boolean {
    return (
      !this.areValuesHidden && !this.localProps.isViewMode && this.isMultiple
    )
  }

  @computed
  private get hasAnyConditionals(): boolean {
    return this.localProps.fieldValues.some((_, i) => {
      const conditionalFields =
        this.localProps.fieldsStore.getAvailableConditionalFields(
          this.localProps.typeField,
          i,
          this.localProps.parentInstanceId,
          this.localProps.parentValueIndex,
        )
      return conditionalFields.length > 0
    })
  }

  @computed
  private get isFieldHeaderShown(): boolean {
    return (
      this.localProps.isHeaderForced ||
      (this.hasMultipleValues &&
        (this.hasAnyConditionals ||
          this.localProps.isForcedTitle ||
          this.areValuesHidden))
    )
  }

  @action.bound
  private toggleValuesHidden() {
    this.areValuesHidden = !this.areValuesHidden
  }
}
